/**
 * Ticket 1.270 (aus 1.265 nach platform gezogen): Anzeigetext der Einheit zu einer Menge aus der Packeinheit des
 * Artikels – eine einzige Regel für Oberfläche (Auftrag, Angebot, Einkauf, Kennzahl-Kachel) und Belege (AB, Rechnung,
 * Lieferschein). Rein, ohne Datenbank, Uhr oder Netz. Nur Text: Menge, Preise und Summen bleiben unberührt.
 */

/** Einheit, wenn der Artikel keine Packeinheit hat. */
export const PIECE_UNIT = 'Stück';

/** Packeinheit eines Artikels (Einzahl/Mehrzahl, Variante erbt vom Hauptartikel); `null` = nicht gepflegt. */
export interface PackUnitFacts {
  packUnit?: string | null;
  packUnitPlural?: string | null;
  /** Ticket 1.270 (Änderung 2): Packeinheit EN aus der Shopware-Übersetzung en-GB; `null` = keine Übersetzung. */
  packUnitEn?: string | null;
  packUnitPluralEn?: string | null;
}

/** Einheit auf englischen Belegen, wenn der Artikel keine Packeinheit hat. */
export const PIECE_UNIT_EN = 'pcs';

/** Belegsprache (wie AB, Rechnung/Proforma und Lieferschein). */
export type DocumentUnitLanguage = 'de' | 'en';

/**
 * Einzahl bei Menge 1, sonst Mehrzahl (fehlt eine Form, die andere); ohne Packeinheit „Stück“. Die Menge kommt als Text
 * („1“, „12“); nur eine ganze Zahl mit dem Wert genau 1 gilt als Einzahl – leer, ungültig oder „1.000“ = Mehrzahl.
 */
export function packUnitText(pack: PackUnitFacts | null | undefined, quantity: string | number | bigint): string {
  const one = pack?.packUnit?.trim() || null;
  const many = pack?.packUnitPlural?.trim() || null;
  return (isOne(quantity) ? (one ?? many) : (many ?? one)) ?? PIECE_UNIT;
}

/** Höchstlänge der Einheit auf einem Beleg (Snapshot-Feld `unit`, `document-snapshot.ts`). */
export const DOCUMENT_UNIT_MAX = 30;

/**
 * Ticket 1.270 (Änderung 2, [RUDOLF 27.10.2026]): Einheit auf einem Beleg in der Belegsprache – eine Regel für AB,
 * Rechnung/Proforma und Lieferschein. Deutsch wie `packUnitText`. Englisch: das englische Paar aus Shopware (en-GB), sonst
 * das deutsche Paar, sonst „pcs“; Einzahl/Mehrzahl wie `packUnitText` (fehlt eine Form, die andere desselben Paars).
 * `fromPackUnit` = der Text stammt aus einer Packeinheit (dann hält die Zeile den E-Rechnungs-Code fest). Ein Text länger
 * als `DOCUMENT_UNIT_MAX` passt nicht in den Beleg: dann der Grundtext („Stück“/„pcs“), nie ein abgeschnittener Text.
 */
export function documentUnit(pack: PackUnitFacts | null | undefined, quantity: string | number | bigint, language: DocumentUnitLanguage): { unit: string; fromPackUnit: boolean } {
  const base = language === 'de' ? PIECE_UNIT : PIECE_UNIT_EN;
  const english = { packUnit: pack?.packUnitEn, packUnitPlural: pack?.packUnitPluralEn };
  const source = language === 'en' && hasPackUnit(english) ? english : hasPackUnit(pack) ? pack : null;
  if (!source) return { unit: base, fromPackUnit: false };
  const unit = packUnitText(source, quantity);
  return unit.length <= DOCUMENT_UNIT_MAX ? { unit, fromPackUnit: true } : { unit: base, fromPackUnit: false };
}

/** Nur der Text von `documentUnit`. */
export function documentUnitText(pack: PackUnitFacts | null | undefined, quantity: string | number | bigint, language: DocumentUnitLanguage): string {
  return documentUnit(pack, quantity, language).unit;
}

/** Hat der Artikel eine (deutsche) Packeinheit (Einzahl oder Mehrzahl, nicht leer)? */
export function hasPackUnit(pack: PackUnitFacts | null | undefined): boolean {
  return Boolean(pack?.packUnit?.trim() || pack?.packUnitPlural?.trim());
}

function isOne(quantity: string | number | bigint): boolean {
  if (typeof quantity === 'bigint') return quantity === 1n;
  if (typeof quantity === 'number') return quantity === 1;
  // Wie 1.265: nur eine ganze Zahl („1“, „01“); „1.000“ ist in deutschen Eingaben tausend, nie eins.
  return /^\d+$/.test(quantity.trim()) && Number(quantity.trim()) === 1;
}

/**
 * Gemeinsame Einheit mehrerer Artikel zu einer Summe (Kennzahl-Kachel, Kopf einer Bestellung): alle ohne Packeinheit
 * (oder leer) → „Stück“; alle mit derselben Packeinheit → deren Text zur Menge; gemischt → `null` (nur die Zahl zeigen,
 * nie eine geratene Einheit – eine Summe aus Packungen und Stück ist keine „Stück“-Zahl).
 */
export function sharedUnitText(
  packs: readonly (PackUnitFacts | null | undefined)[],
  quantity: string | number | bigint,
): string | null {
  const key = (p: PackUnitFacts | null | undefined) => `${p?.packUnit?.trim() || ''}\u0000${p?.packUnitPlural?.trim() || ''}`;
  const keys = new Set(packs.map(key));
  if (keys.size > 1) return null;
  return packUnitText(packs[0], quantity);
}
